import React, { useRef, useState, useEffect } from 'react';
import { Input, DatePicker, Select as AntdSelect } from 'antd';
import clsx from 'clsx';
import moment from 'moment';
import Icon from 'component/icon';

const SingleDate = (props) => {
    const { filter } = props;
    const [date, setDate] = useState('');

    useEffect(() => {
        setDate(filter.value);
    }, [filter.value]);

    return (
        <>
            <div>{filter.label ?? '日期'}</div>
            <DatePicker
                bordered={false}
                placeholder=""
                allowClear={false}
                suffixIcon={null}
                className="mx-1 w-[100px]"
                value={date}
                onChange={(v) => {
                    setDate(v);
                    filter.onChangeDate?.(v);
                }}
            />
            {!!filter.onSubmit && (
                <Icon
                    name="search"
                    className="mr-1 cursor-pointer text-table-tab-selected"
                    onClick={() => filter.onSubmit?.(date)}
                />
            )}
        </>
    );
};

const Date = (props) => {
    const { filter, dateTime } = props;
    const [optionValue, setOptionValue] = useState(filter.optionValue);
    const [date, setDate] = useState(filter.value || []);
    const clickDate = useRef(null);

    useEffect(() => {
        setOptionValue(filter.optionValue);
    }, [filter.optionValue]);
    useEffect(() => {
        setDate(filter.value || []);
    }, [filter.value]);

    return (
        <>
            {filter.options ? (
                <AntdSelect
                    bordered={false}
                    dropdownMatchSelectWidth={false}
                    options={filter.options}
                    value={optionValue}
                    onChange={(v) => {
                        setOptionValue(v);
                        filter.onChangeOption?.(v);
                    }}
                />
            ) : (
                <div>{filter.label ?? '日期'}</div>
            )}
            <DatePicker.RangePicker
                bordered={false}
                placeholder=""
                allowClear={false}
                suffixIcon={null}
                separator="-"
                className={clsx(
                    'mx-1',
                    dateTime ? 'w-[300px]' : 'w-[200px]'
                )}
                value={date}
                showTime={dateTime}
                format={dateTime ? 'YYYY-MM-DD HH:mm' : undefined}
                onChange={(v) => {
                    let values = v;
                    if (filter.maxRange && clickDate.current && v.length === 2) {
                        const { range, value } = clickDate.current;
                        if (range === 'start') {
                            const max = moment(value).add(...filter.maxRange).subtract(1, 'd');
                            if (v[1].isAfter(max, 'd')) {
                                values = [v[0], max];
                            }
                        } else if (range === 'end') {
                            const min = moment(value).subtract(...filter.maxRange).add(1, 'd');
                            if (v[0].isBefore(min, 'd')) {
                                values = [min, v[1]];
                            }
                        }
                    }
                    setDate(values);
                    filter.onChangeDate?.(values);
                }}
                onCalendarChange={(_, v, { range }) => {
                    clickDate.current = { range, value: range === 'start' ? v[0] : v[1] };
                }}
                onOpenChange={(open) => {
                    if (!open) {
                        clickDate.current = null;
                    }
                }}
                disabledDate={(curr) => {
                    let disabled = false;
                    if (filter.maxRange && clickDate.current) {
                        const { range, value } = clickDate.current;
                        if (range === 'start') {
                            disabled = curr.isAfter(moment(value).add(...filter.maxRange).subtract(1, 'd'), 'd');
                        } else if (range === 'end') {
                            disabled = curr.isBefore(moment(value).subtract(...filter.maxRange).add(1, 'd'), 'd');
                        }
                    }
                    // [Moment, Moment]
                    if (filter.dateRange) {
                        disabled = curr.isBefore(moment(filter.dateRange[0]), 'd') ||
                            curr.isAfter(moment(filter.dateRange[1]), 'd');
                    }
                    return disabled;
                }}
            />
            {!!filter.onSubmit && (
                <Icon
                    name="search"
                    className="mr-1 cursor-pointer text-table-tab-selected"
                    onClick={() => {
                        if (date.length === 2) {
                            filter.onSubmit?.(date);
                        }
                    }}
                />
            )}
        </>
    );
};

const Search = (props) => {
    const { filter } = props;
    const [text, setText] = useState('');

    useEffect(() => {
        setText(filter.value);
    }, [filter.value]);

    return (
        <>
            <div>{filter.label ?? '搜尋'}</div>
            <div className="mx-1">
                <Input
                    bordered={false}
                    placeholder={filter.placeholder ?? '請輸入關鍵字'}
                    className="w-[180px] bg-gray-light01"
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        filter.onChange?.(e.target.value);
                    }}
                    onPressEnter={() => filter.onSubmit?.(text)}
                />
            </div>
            {!!filter.onSubmit && (
                <Icon
                    name="search"
                    className="cursor-pointer text-table-tab-selected"
                    onClick={() => filter.onSubmit?.(text)}
                />
            )}
        </>
    );
};

const Select = (props) => {
    const { filter } = props;
    const [value, setValue] = useState('');

    useEffect(() => {
        setValue(filter.value);
    }, [filter.value]);

    return (
        <>
            {!!filter.label && <div>{filter.label}</div>}
            <div className="mx-1">
                <AntdSelect
                    bordered={false}
                    className="w-[180px] bg-gray-light01"
                    options={filter.options}
                    placeholder={filter.placeholder}
                    allowClear
                    showSearch
                    filterOption={(input, option) => (
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    )}
                    value={value}
                    onChange={(v) => {
                        setValue(v);
                        filter.onChange?.(v);
                    }}
                />
            </div>
            {!!filter.onSubmit && (
                <Icon
                    name="search"
                    className="cursor-pointer text-table-tab-selected"
                    onClick={() => filter.onSubmit?.(value)}
                />
            )}
        </>
    );
};

const TableFilter = (props) => {
    const { filters, wrap } = props;

    return !!filters?.length && (
        <div className={clsx(
            'table-filter overflow-x-auto overflow-y-hidden hidden-scrollbar',
            wrap && 'flex flex-wrap gap-y-[6px]'
        )}
        >
            {filters.map((item, index) => item.isShow !== false && (
                <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className={clsx(
                        'flex items-center h-full',
                        {
                            'border-r-2 border-gray pr-1 mr-2':
                                index !== filters.length - 1 && item.nextBorder !== false
                        }
                    )}
                >
                    {(() => {
                        switch (item.type) {
                            case 'SingleDate':
                                return <SingleDate filter={item} />;
                            case 'Date':
                                return <Date filter={item} />;
                            case 'DateTime':
                                return <Date filter={item} dateTime />;
                            case 'Search':
                                return <Search filter={item} />;
                            case 'Select':
                                return <Select filter={item} />;
                            case 'Custom':
                                return item.render?.();
                            default:
                                return null;
                        }
                    })()}
                </div>
            ))}
        </div>
    );
};

export default TableFilter;
